import {Alert, MultiChoice} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNotEmpty} from 'ramda';

import {SALE_VEHICLE_WATERMARKS_MAX_COUNT} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {getWatermarkCapState} from '../utils/getWatermarkCapState';

type WatermarkOption = {
  label: string;
  value: string;
};

interface VehicleWatermarkChoiceProps extends RequiredTestIdProps {
  options: WatermarkOption[];
  value: string[];
  effectiveWatermarkIds: string[];
  isLoading: boolean;
  onChange: (watermarkIds: string[]) => void;
}

// The BE keeps at most SALE_VEHICLE_WATERMARKS_MAX_COUNT effective watermarks and inherited ones
// come first, so the picker stops at the slots that are left for the vehicle's own watermarks.
export function VehicleWatermarkChoice(props: VehicleWatermarkChoiceProps) {
  const {inheritedCount, ownCap, cutIds} = getWatermarkCapState(
    props.value,
    props.effectiveWatermarkIds,
    SALE_VEHICLE_WATERMARKS_MAX_COUNT
  );
  const isLimitReached = props.value.length >= ownCap;

  const options = props.options.map((option) => ({
    ...option,
    isDisabled: isLimitReached && !props.value.includes(option.value),
  }));

  const limitHint =
    inheritedCount > 0
      ? i18n.t('entity.watermark.labels.maxWatermarksWithInheritedHint', {
          count: SALE_VEHICLE_WATERMARKS_MAX_COUNT,
          inherited: inheritedCount,
        })
      : i18n.t('entity.watermark.labels.maxWatermarksHint', {
          count: SALE_VEHICLE_WATERMARKS_MAX_COUNT,
        });

  const cutWatermarkNames = props.options
    .filter((option) => cutIds.includes(option.value))
    .map((option) => option.label);

  return (
    <VStack spacing={2}>
      <MultiChoice
        options={options}
        value={props.value}
        onChange={(value) => props.onChange(value ?? [])}
        isLoading={props.isLoading}
        isDisabled={props.options.length === 1 || props.isLoading}
        helperText={isLimitReached ? limitHint : undefined}
        isNotClearable
        data-testid={props['data-testid']}
      />
      <Show when={isNotEmpty(cutIds)}>
        <Alert
          type="inline"
          variant="warning"
          title={i18n.t('entity.watermark.labels.watermarkStackFull', {count: cutIds.length})}
          message={cutWatermarkNames.join(', ')}
          data-testid={suffixTestId('stackFullAlert', props)}
        />
      </Show>
    </VStack>
  );
}
